import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useAuthSession } from '../contexts/AuthContext.jsx';
import { useToast } from '../contexts/ToastContext.jsx';

/**
 * AuthCallback — landing page for the Google OAuth redirect.
 *
 * Reads ``?token=<jwt>`` (success) or ``?error=<code>&message=<msg>``
 * (failure) off the URL. On success: stores the token in the auth
 * context (which also writes to localStorage and fetches /auth/me),
 * then redirects by role. On failure: routes back to /login with an
 * error toast.
 */
export default function AuthCallback() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { loginWithToken } = useAuthSession();

  // Strict mode double-invokes effects in dev; guard so we only handle
  // the callback once per mount.
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;

    const token = params.get('token');
    const error = params.get('error');
    const message = params.get('message');

    if (error || !token) {
      const msg =
        message ||
        (error === 'unauthorized'
          ? 'Your account is not authorized. Contact your administrator.'
          : 'Sign-in failed. Please try again.');
      showToast(msg, { type: 'error' });
      navigate('/login', { replace: true });
      return;
    }

    (async () => {
      try {
        const user = await loginWithToken(token);
        showToast('Signed in', { type: 'success' });
        const target =
          user?.role === 'super_admin' ? '/admin/clients' : '/dashboard';
        navigate(target, { replace: true });
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const msg =
          typeof detail === 'string'
            ? detail
            : err?.message || 'Sign-in failed. Please try again.';
        showToast(msg, { type: 'error' });
        navigate('/login', { replace: true });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      className="min-h-screen w-full flex items-center justify-center text-white"
      style={{ backgroundColor: '#000000' }}
    >
      <div className="text-[14px] text-[#AAACBA]">Completing sign-in…</div>
    </div>
  );
}
